import { useState } from "react";
import {
  MessageSquare,
  Info,
  Lightbulb,
  CheckSquare,
  PenLine,
  Mail,
  FileText,
  Plus,
  Copy,
  GitBranch,
  Play,
  MousePointer,
  X,
  Search,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { Button } from "../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { Input } from "../../../components/ui/input";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { config } from "../../../Environment";
import TakeawaysRunningActivity from "./takeaways";

const ChatPanel = ({
  processId,
  isMobile,
}: {
  isMobile: boolean;
  processId: string;
}) => {
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(
    null
  );
  const [selectedMessageIndex, setSelectedMessageIndex] = useState<
    number | null
  >(null);
  const [newMessageScope, setNewMessageScope] = useState("direct");
  const [showScopeSelector, setShowScopeSelector] = useState(false);
  const [selectingComponent, setSelectingComponent] = useState(false);

  const [noteType, setNoteType] = useState("");

  // Mock data for take-aways
  const [takeAways, setTakeAways] = useState([
    { type: "action", content: "My name is umer" },
    {
      type: "insight",
      content: "I am a web developer",
    },
    {
      type: "action",
      content: "I am from Pakistan",
    },
  ]);

  // Mock data for chat messages
  const [chatMessages, setChatMessages] = useState([
    {
      scope: "general",
      content: "What's up?",
      user: "system",
      timestamp: "10:30 AM",
    },
    {
      scope: "workflow",
      content: "I am good",
      user: "jane",
      timestamp: "10:35 AM",
    },
    {
      scope: "activity",
      content: "How is weather?",
      user: "john",
      timestamp: "10:40 AM",
    },
  ]);

  // Mock data for feedback entries
  const [feedbackEntries, setFeedbackEntries] = useState([
    {
      context: "workflow",
      content: "Not Good.",
      user: "jane",
    },
    {
      context: "activity",
      content: "Good Activity.",
      user: "john",
    },
    {
      context: "control",
      content: "Excellent Activity",
      user: "alex",
    },
  ]);

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case "direct":
        return <MessageSquare className="h-4 w-4" />;
      case "general":
        return <Info className="h-4 w-4" />;
      case "workflow":
        return <GitBranch className="h-4 w-4" />;
      case "engagement":
        return <Play className="h-4 w-4" />;
      case "activity":
        return <FileText className="h-4 w-4" />;
      case "control":
        return <MousePointer className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  return (
    <div
      className={`flex flex-col h-full bg-white overflow-y-auto ${
        isMobile ? "" : "w-96 border-l fixed right-0"
      }`}
    >
      <div className="p-4 border-b">
        {/* <Tabs defaultValue="takeaways">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="takeaways" className="flex items-center gap-1">
              <Lightbulb className="h-4 w-4" />
              <span>Take-Aways</span>
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />
              <span>Chat</span>
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex items-center gap-1">
              <PenLine className="h-4 w-4" />
              <span>Feedback</span>
            </TabsTrigger>
          </TabsList> */}

        {/* Take-Aways Tab */}
        {/* <TabsContent value="takeaways" className="mt-4"> */}
        <TakeawaysRunningActivity processId={processId} isMobile={isMobile} />
        {/* </TabsContent> */}

        {/* Chat Tab */}
        {/* <TabsContent value="chat" className="mt-4">
            <div className="space-y-4">
              {chatMessages.length === 0 ? (
                <div className="p-4 bg-muted rounded-lg text-center space-y-3">
                  <p className="text-sm">Get expert help with this activity.</p>
                  <Button>Get Paid Support</Button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {chatMessages.map((message, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 relative group"
                      onClick={() =>
                        setSelectedMessageIndex(
                          selectedMessageIndex === index ? null : index
                        )
                      }
                    >
                      <div className="shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-muted">
                        {getScopeIcon(message.scope)}
                      </div>

                      <div className="flex-1">
                        <p className="text-sm">{message.content}</p>
                      </div>

                      <div className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground">
                        <span>{message.timestamp}</span>
                        <Avatar className="h-6 w-6">
                          <AvatarImage
                            src={`/placeholder.svg?height=24&width=24&text=${message.user
                              .charAt(0)
                              .toUpperCase()}`}
                          />
                          <AvatarFallback>
                            {message.user.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>

                      {selectedMessageIndex === index && (
                        <div className="absolute right-0 top-full mt-1 z-10 bg-popover border rounded-md shadow-md p-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              addToTakeAways(message.content);
                              setSelectedMessageIndex(null);
                            }}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1.5" />
                            Copy to Take-Aways
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                {showScopeSelector && (
                  <div className="p-2 border rounded-md bg-muted/50">
                    <p className="text-xs font-medium mb-2">
                      Select message scope:
                    </p>
                    <div className="grid grid-cols-3 gap-1">
                      <Button
                        variant={
                          newMessageScope === "direct" ? "default" : "outline"
                        }
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => {
                          setNewMessageScope("direct");
                          setShowScopeSelector(false);
                          setSelectingComponent(false);
                        }}
                      >
                        <MessageSquare className="h-3.5 w-3.5 mr-1" />
                        Direct
                      </Button>
                      <Button
                        variant={
                          newMessageScope === "general" ? "default" : "outline"
                        }
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => {
                          setNewMessageScope("general");
                          setShowScopeSelector(false);
                          setSelectingComponent(false);
                        }}
                      >
                        <Info className="h-3.5 w-3.5 mr-1" />
                        General
                      </Button>
                      <Button
                        variant={
                          newMessageScope === "workflow" ? "default" : "outline"
                        }
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => {
                          setNewMessageScope("workflow");
                          setShowScopeSelector(false);
                          setSelectingComponent(false);
                        }}
                      >
                        <GitBranch className="h-3.5 w-3.5 mr-1" />
                        Workflow
                      </Button>
                      <Button
                        variant={
                          newMessageScope === "engagement"
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => {
                          setNewMessageScope("engagement");
                          setShowScopeSelector(false);
                          setSelectingComponent(false);
                        }}
                      >
                        <Play className="h-3.5 w-3.5 mr-1" />
                        Engagement
                      </Button>
                      <Button
                        variant={
                          newMessageScope === "activity" ? "default" : "outline"
                        }
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => {
                          setNewMessageScope("activity");
                          setShowScopeSelector(false);
                          setSelectingComponent(false);
                        }}
                      >
                        <FileText className="h-3.5 w-3.5 mr-1" />
                        Activity
                      </Button>
                      <Button
                        variant={
                          newMessageScope === "control" ? "default" : "outline"
                        }
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => {
                          setNewMessageScope("control");
                          setShowScopeSelector(false);
                          setSelectingComponent(true);
                        }}
                      >
                        <MousePointer className="h-3.5 w-3.5 mr-1" />
                        Control
                      </Button>
                    </div>
                  </div>
                )}

                {selectingComponent && (
                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
                    <p>Select a component to link this message.</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-1"
                      onClick={() => setSelectingComponent(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => setShowScopeSelector(!showScopeSelector)}
                  >
                    {getScopeIcon(newMessageScope)}
                  </Button>

                  <div className="flex-1">
                    <Textarea
                      placeholder="Type your message here..."
                      className="resize-none min-h-[80px]"
                      disabled={selectingComponent}
                    />
                    <div className="flex justify-between mt-2">
                      <div className="text-xs text-muted-foreground">
                        {newMessageScope.charAt(0).toUpperCase() +
                          newMessageScope.slice(1)}{" "}
                        message
                      </div>
                      <Button size="sm">Send</Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent> */}

        {/* Feedback Tab */}
        {/* <TabsContent value="feedback" className="mt-4">
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Activity Feedback</h3>

              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {feedbackEntries.map((feedback, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-2 rounded-md border hover:bg-muted/50"
                  >
                    <div className="shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-muted">
                      {getScopeIcon(feedback.context)}
                    </div>

                    <div className="flex-1">
                      <p className="text-sm">{feedback.content}</p>
                    </div>

                    <Avatar className="h-6 w-6 shrink-0">
                      <AvatarImage
                        src={`/placeholder.svg?height=24&width=24&text=${feedback.user
                          .charAt(0)
                          .toUpperCase()}`}
                      />
                      <AvatarFallback>
                        {feedback.user.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <p className="text-sm">
                  Add your feedback about this activity:
                </p>
                <Textarea
                  placeholder="Share your thoughts..."
                  className="min-h-[100px] resize-none"
                />

                <div className="flex justify-between items-center">
                  

                  <Button size="sm">Submit Feedback</Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs> */}
      </div>
    </div>
  );
};

export default ChatPanel;
